import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Amplify, Auth } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { validateEnvVars, setupTokenRefresh } from './utils/securityUtils';
import Header from './components/Header';
import Footer from './components/Footer';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

// Lazy load heavy components
const Gallery = lazy(() => import('./components/Gallery'));
const UploadForm = lazy(() => import('./components/UploadForm'));
const InstaTrans = lazy(() => import('./components/InstaTrans/InstaTrans'));

// Validate environment variables
if (!validateEnvVars()) {
  console.error('Application cannot start due to missing environment variables');
}

// Create a global API access object for guest/public access
// This avoids using Amplify API for unauthenticated users entirely
window.publicApi = {
  // Base API endpoint from env
  endpoint: process.env.REACT_APP_API_URL || '',

  // GET method that doesn't require auth
  get: async (path, queryParams = {}) => {
    try {
      // Build query string
      const queryString = Object.keys(queryParams).length > 0
        ? '?' + new URLSearchParams(queryParams).toString()
        : '';

      // Ensure endpoint doesn't have trailing slash and path starts with one
      const cleanEndpoint = window.publicApi.endpoint.replace(/\/$/, '');
      const cleanPath = path.startsWith('/') ? path : `/${path}`;
      const finalUrl = `${cleanEndpoint}${cleanPath}${queryString}`;

      try {
        // Make direct fetch request
        const response = await fetch(finalUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
            // Removed 'Cache-Control': 'no-cache' to fix CORS preflight issue
          }
        });

        if (!response.ok) {
          throw new Error(`Public API request failed: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        // Check if it's a CORS error and if we're using excludeKeys
        if (error.message && (error.message.includes('Network Error') || error.message.includes('CORS') || error.message.includes('Failed to fetch')) &&
            queryParams.excludeKeys) {
          console.warn('CORS or network error detected in publicApi.get, retrying without excludeKeys:', error);

          // Remove excludeKeys and retry
          const simpleParams = {...queryParams};
          delete simpleParams.excludeKeys;

          // Build a new query string without excludeKeys
          const simpleQueryString = Object.keys(simpleParams).length > 0
            ? '?' + new URLSearchParams(simpleParams).toString()
            : '';

          const simpleUrl = `${cleanEndpoint}${cleanPath}${simpleQueryString}`;

          // Retry the request without excludeKeys
          const retryResponse = await fetch(simpleUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });

          if (!retryResponse.ok) {
            throw new Error(`Retry request failed: ${retryResponse.status}`);
          }

          console.log('Successfully fetched data without excludeKeys after CORS error');
          return await retryResponse.json();
        }

        // If it's not a CORS error or we're not using excludeKeys, rethrow
        throw error;
      }
    } catch (error) {
      console.error('Public API error:', error);
      throw error;
    }
  }
};

// Initialize Amplify for authenticated users only
Amplify.configure({
  Auth: {
    region: process.env.REACT_APP_REGION,
    userPoolId: process.env.REACT_APP_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
    identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
    // Only store auth state in cookies for authenticated sessions
    cookieStorage: {
      domain: window.location.hostname,
      secure: window.location.protocol === 'https:',
      path: '/',
      expires: 7 // 7 days for authenticated users
    },
    authenticationFlowType: 'USER_SRP_AUTH'
  },
  API: {
    endpoints: [
      {
        name: 'photoGalleryApi',
        endpoint: process.env.REACT_APP_API_URL,
        // Only include auth headers for authenticated users
        custom_header: async () => {
          try {
            const session = await Auth.currentSession();
            return {
              Authorization: `Bearer ${session.getIdToken().getJwtToken()}`
            };
          } catch (error) {
            // If we can't get a session, return empty headers
            // This should rarely happen since we'll use publicApi for guests
            return {};
          }
        },
      },
    ],
  },
});

function App() {
  const [isUploading, setIsUploading] = useState(false);
  const [refreshGallery, setRefreshGallery] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [newUploadedImages, setNewUploadedImages] = useState([]);

  // Check authentication status and setup token refresh
  useEffect(() => {
    // Wrap in try/catch to prevent unhandled errors
    try {
      console.log('App initializing...');
      checkAuthStatus();

      // Setup token refresh mechanism
      const tokenRefreshInterval = setupTokenRefresh();

      // Mark as initialized after a short delay to ensure all components are ready
      const initTimer = setTimeout(() => {
        setIsInitialized(true);
        console.log('App fully initialized');
      }, 500);

      // Cleanup on unmount
      return () => {
        if (tokenRefreshInterval) clearInterval(tokenRefreshInterval);
        clearTimeout(initTimer);
      };
    } catch (err) {
      console.error('Error during initialization:', err);
      // Still mark as initialized even if there was an error
      setIsInitialized(true);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      const userData = await Auth.currentAuthenticatedUser();
      setIsAuthenticated(true);
      setUser(userData);
    } catch (err) {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const handleUploadSuccess = (uploadedImages) => {
    console.log("Upload success with images:", uploadedImages);

    // Store the newly uploaded images data immediately
    setNewUploadedImages(uploadedImages);

    // Reset the uploaded images after a delay to prevent duplicate additions
    setTimeout(() => {
      setNewUploadedImages([]);
    }, 5000);

    // Stop showing the upload form
    setIsUploading(false);

    // Then trigger gallery refresh as a backup mechanism
    setTimeout(() => {
      setRefreshGallery(prev => !prev);
    }, 100);
  };

  const handleSignOut = async () => {
    try {
      await Auth.signOut();
      setIsAuthenticated(false);
      setUser(null);
      setIsUploading(false);
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  return (
    <ThemeProvider>
      <Router>
        <div className="app-container">
          <Header
            user={user}
            isAuthenticated={isAuthenticated}
            signOut={handleSignOut}
            setIsUploading={isAuthenticated ? setIsUploading : null}
          />

          <main className="w-full">
            <Suspense fallback={<div className="p-8 text-center">Loading...</div>}>
              {!isInitialized ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center py-8 px-8 rounded-lg bg-card-bg shadow-lg">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-link-color mx-auto"></div>
                    <p className="mt-4 text-lg font-semibold text-text-color">Initializing application...</p>
                  </div>
                </div>
              ) : isAuthenticated && isUploading ? (
                <UploadForm onUploadSuccess={handleUploadSuccess} onCancel={() => setIsUploading(false)} />
              ) : (
                <Routes>
                  <Route path="/" element={
                    <Gallery
                      refreshTrigger={refreshGallery}
                      isAuthenticated={isAuthenticated}
                      isAppInitialized={isInitialized}
                      newUploadedImages={newUploadedImages}
                    />
                  } />
                <Route path="/login" element={
                  <div className="max-w-md mx-auto my-8 p-6 card-bg rounded-lg shadow-lg">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-2xl font-bold text-center flex-grow">Sign In</h2>
                      <button
                        onClick={() => window.history.back()}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                        aria-label="Close"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="amplify-auth-container">
                      <Authenticator>
                        {({ user, signOut }) => {
                          // When authentication succeeds, update state and redirect
                          setIsAuthenticated(true);
                          setUser(user);
                          return <Navigate to="/" replace />;
                        }}
                      </Authenticator>
                    </div>
                    <div className="mt-4 text-center">
                      <button
                        onClick={() => window.history.back()}
                        className="text-sm text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Back to Gallery
                      </button>
                    </div>
                  </div>
                } />
                <Route path="/instatrans" element={<InstaTrans />} />
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              )}
            </Suspense>
          </main>

          <Footer />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
